'use client';

import * as React from 'react';
import Area from './Area';
import { Canvas, GroupProps, PrimitiveProps, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { Group } from 'three';
import glbBustHi from './bust-hi.glb';
import glbBustLo from './bust-lo-draco.glb';
import { QueryClient, QueryClientProvider, useSuspenseQuery } from '@tanstack/react-query';

/**
 * React component that trigger suspense until `milliseconds` is reached
 * @param props
 * @param props.milliseconds - milliseconds to wait before resolving the suspense
 * @param props.children - children to render
 */
// This component creates a promise that resolves after the specified milliseconds
// and throws that promise until it resolves, triggering Suspense
function DelayedReveal({ milliseconds = 2000, children }: React.PropsWithChildren<{ milliseconds?: number }>) {
  // Track whether we've waited long enough
  const [isReady, setIsReady] = React.useState(false);
  // Store the promise in a ref so we always throw the same promise instance
  const [promise] = React.useState(
    () =>
      new Promise<void>((resolve) => {
        setTimeout(() => {
          setIsReady(true);
          resolve();
        }, milliseconds);
      })
  );

  console.log('DelayedReveal', { milliseconds, isReady });

  // If we haven't waited long enough, throw the promise to trigger Suspense
  if (!isReady) {
    throw promise;
  }

  // Once we've waited long enough, render the children
  return children;
}

function Delay({
  name,
  milliseconds = 1000,
  children,
}: React.PropsWithChildren<{ name: string; milliseconds?: number }>) {
  useSuspenseQuery({
    queryKey: ['delay', name],
    queryFn: () => new Promise((resolve) => setTimeout(() => resolve(true), milliseconds)),
    // refetchOnMount: true,
  });
  return children;
}

function Model({ url, ...props }: Omit<PrimitiveProps, 'object'> & { url: string }) {
  // useGLTF suspends the component, it literally stops processing
  const { scene } = useGLTF(url);
  // By the time we're here the model is gueranteed to be available
  return <primitive object={scene} {...props} />;
}

function Rotate(props: GroupProps) {
  const ref = React.useRef<Group>(null);
  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.x = state.clock.elapsedTime;
    }
  });
  return <group ref={ref} {...props} />;
}

export type Props = {};

const client = new QueryClient();

function App01(props: Props) {
  const {} = props;

  const [count, setCount] = React.useState(0);
  return (
    <QueryClientProvider key={count} client={client}>
      <Area className="relative">
        <React.Suspense
          fallback={<div className="absolute w-full flex h-full justify-center items-center">Loading...</div>}
        >
          <Delay name="level 1" milliseconds={2000} />
          <Canvas camera={{ position: [-2, 2, 4], fov: 25 }}>
            <directionalLight position={[10, 10, 0]} intensity={1.5} />
            <directionalLight position={[-10, 10, 5]} intensity={1} />
            <directionalLight position={[-10, 20, 0]} intensity={1.5} />
            <directionalLight position={[0, -10, 0]} intensity={0.25} />
            <Rotate position-y={-0.5} scale={0.2}>
              <React.Suspense fallback={<Model url={glbBustLo} />}>
                <Delay name="level 2" milliseconds={2000} />
                <Model url={glbBustHi} />
              </React.Suspense>
            </Rotate>
          </Canvas>
        </React.Suspense>
        <div className="absolute bottom-0 w-full flex justify-end">
          <button
            onClick={() => {
              client.clear();
              setCount((c) => c + 1);
            }}
            className="m-2 p-2 bg-blue-500 text-white rounded"
          >
            Show again
          </button>
        </div>
      </Area>
    </QueryClientProvider>
  );
}

export default React.memo(App01);
